import { Geodesic } from "geographiclib-geodesic";

// Initial bearing between two GPS points on the WGS84 ellipsoid.
// Returns an angle in degrees in [0, 360[: 0 = North, 90 = East, 180 = South, 270 = West.
export const getBearing = (
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number,
): number => {
  const { azi1 } = Geodesic.WGS84.Inverse(lat1, lon1, lat2, lon2);
  return ((azi1 ?? 0) + 360) % 360;
};

// Returns true if the vehicle is currently on a trip (last data point is more recent than all completed trip end dates).
export const isTripOngoing = (
  lastDataPointDate: string | undefined,
  completedTrips: { endDate: string }[],
): boolean => {
  if (!lastDataPointDate) return false;
  if (completedTrips.length === 0) return true;
  const lastEndDate = Math.max(
    ...completedTrips.map((t) => new Date(t.endDate).getTime()),
  );
  return new Date(lastDataPointDate).getTime() > lastEndDate;
};

// Bearing from the last two data points — used to orient the car icon when a trip is ongoing.
export const getCarBearing = (
  dataPoints: { lat: number; lon: number }[],
): number => {
  if (dataPoints.length < 2) return 0;
  const p1 = dataPoints[dataPoints.length - 2];
  const p2 = dataPoints[dataPoints.length - 1];
  return getBearing(p1.lat, p1.lon, p2.lat, p2.lon);
};

type DataPoint = { lat: number; lon: number; date: string };

// For each trip boundary (except the last), returns the data point closest in time to the trip's end date.
// Used to place the junction circle icon between consecutive trips.
export const getJunctionDataPoints = <T extends DataPoint>(
  dataPoints: T[],
  trips: { endDate: string }[],
): T[] => {
  if (dataPoints.length === 0 || trips.length <= 1) return [];
  return trips.slice(0, -1).map((trip) => {
    const endTime = new Date(trip.endDate).getTime();
    return dataPoints.reduce((closest, dp) =>
      Math.abs(new Date(dp.date).getTime() - endTime) <
      Math.abs(new Date(closest.date).getTime() - endTime)
        ? dp
        : closest,
    );
  });
};

const ARROW_INTERVAL_M = 3000;

// Places one arrow every ARROW_INTERVAL_M, starting at half the interval.
// Falls back to the midpoint if the segment is shorter than the interval.
const getSegmentArrows = (
  points: { lat: number; lon: number }[],
): { lat: number; lon: number; bearing: number }[] => {
  if (points.length < 2) return [];
  const arrows: { lat: number; lon: number; bearing: number }[] = [];
  let accumulated = ARROW_INTERVAL_M / 2;
  for (let i = 0; i < points.length - 1; i++) {
    const p1 = points[i];
    const p2 = points[i + 1];
    const { s12 } = Geodesic.WGS84.Inverse(p1.lat, p1.lon, p2.lat, p2.lon);
    accumulated += s12 ?? 0;
    if (accumulated >= ARROW_INTERVAL_M) {
      arrows.push({
        lat: p1.lat,
        lon: p1.lon,
        bearing: getBearing(p1.lat, p1.lon, p2.lat, p2.lon),
      });
      accumulated = 0;
    }
  }
  if (arrows.length === 0) {
    const midIdx = Math.floor((points.length - 1) / 2);
    const p1 = points[midIdx];
    const p2 = points[midIdx + 1];
    arrows.push({
      lat: p1.lat,
      lon: p1.lon,
      bearing: getBearing(p1.lat, p1.lon, p2.lat, p2.lon),
    });
  }
  return arrows;
};

// For each trip segment, places directional arrows every 3km.
// Falls back to one arrow at the midpoint for short segments.
// If trips is empty, applies the logic to all datapoints.
export const getArrowPositions = (
  dataPoints: DataPoint[],
  trips: { startDate: string; endDate: string }[],
): { lat: number; lon: number; bearing: number }[] => {
  if (dataPoints.length < 2) return [];
  if (trips.length === 0) {
    const sorted = [...dataPoints]
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter(
        (dp, i, arr) =>
          i === 0 || dp.lat !== arr[i - 1].lat || dp.lon !== arr[i - 1].lon,
      );
    return getSegmentArrows(sorted);
  }
  return trips.flatMap((trip) => {
    const startTime = new Date(trip.startDate).getTime();
    const endTime = new Date(trip.endDate).getTime();
    const segmentPoints = dataPoints
      .filter((dp) => {
        const t = new Date(dp.date).getTime();
        return t >= startTime && t <= endTime;
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
      .filter(
        (dp, i, arr) =>
          i === 0 || dp.lat !== arr[i - 1].lat || dp.lon !== arr[i - 1].lon,
      );
    return getSegmentArrows(segmentPoints);
  });
};
